import { useEffect, useRef } from 'react';
import { getReleaseDateRaw } from '../config/releaseDate';
import toast from '../services/toast';

const POLL_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes
const RELOAD_DELAY_MS = 3000;

async function fetchServerReleaseDate(): Promise<string | null> {
  try {
    const res = await fetch('/version.json', { cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    const releaseDate = typeof data?.releaseDate === 'string' ? data.releaseDate.trim() : '';
    return releaseDate || null;
  } catch {
    return null;
  }
}

/**
 * Polls /version.json (written alongside releaseDateStamp.ts by scripts/deploy-to-vps.sh) and
 * force-reloads the tab when it no longer matches the release stamp baked into this bundle —
 * so an already-open tab picks up a new deploy without the user needing to hard refresh.
 */
export function useDeployVersionWatcher() {
  const reloadingRef = useRef(false);

  useEffect(() => {
    const bundledReleaseDate = getReleaseDateRaw();
    // No stamp baked in (e.g. plain local dev) — nothing to compare against.
    if (!bundledReleaseDate) return undefined;

    const checkForNewVersion = async () => {
      if (reloadingRef.current) return;
      const serverReleaseDate = await fetchServerReleaseDate();
      if (!serverReleaseDate || serverReleaseDate === bundledReleaseDate) return;
      reloadingRef.current = true;
      toast.info('A new version of VantagePoint is available. Refreshing…');
      setTimeout(() => window.location.reload(), RELOAD_DELAY_MS);
    };

    const intervalId = setInterval(() => void checkForNewVersion(), POLL_INTERVAL_MS);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void checkForNewVersion();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);
}

export default useDeployVersionWatcher;
